// Styled replacement for the native window.confirm that Turbo fires on
// `data-turbo-confirm`. Returns a Promise<boolean>. One reusable modal
// is appended to <body> on first use. Honors the project convention:
// no browser confirm() — always a styled modal with backdrop, title,
// message, Cancel/Confirm.
//
// Wired app-wide in application.js via Turbo.config.forms.confirm, so
// every existing `data: { turbo_confirm: "…" }` button/link gets it for
// free — no per-button changes.

let overlay = null

function build() {
  const el = document.createElement("div")
  el.className =
    "hidden fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4"
  el.innerHTML = `
    <div data-tc-panel class="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
      <h3 data-tc-title class="text-base font-semibold text-gray-900 mb-1">Please confirm</h3>
      <p data-tc-message class="text-[13px] text-gray-600 mb-4 whitespace-pre-line"></p>
      <div class="flex justify-end gap-2">
        <button type="button" data-tc-cancel
                class="px-3 py-1.5 text-[13px] font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg cursor-pointer">Cancel</button>
        <button type="button" data-tc-confirm
                class="px-3 py-1.5 text-[13px] font-medium text-white rounded-lg cursor-pointer">Confirm</button>
      </div>
    </div>`
  document.body.appendChild(el)
  return el
}

export function styledConfirm(message, trigger) {
  return new Promise((resolve) => {
    // A Turbo body swap (e.g. saving an inline minutes edit navigates via
    // Turbo Drive) detaches the cached overlay — the next confirm would
    // then "open" an off-DOM modal and the button looks dead until a full
    // refresh (Kloepfer 2026-07-31). Rebuild whenever it's disconnected.
    if (!overlay || !overlay.isConnected) overlay = build()

    const panel = overlay.querySelector("[data-tc-panel]")
    const msgEl = overlay.querySelector("[data-tc-message]")
    const confirmBtn = overlay.querySelector("[data-tc-confirm]")
    const cancelBtn = overlay.querySelector("[data-tc-cancel]")

    msgEl.textContent = message || "Are you sure?"

    // Per-trigger button labels, so an action whose own name is "Cancel"
    // doesn't end up with an ambiguous "Cancel / Confirm" dialog. Set
    // data-confirm-label / data-cancel-label on the button to override.
    confirmBtn.textContent = trigger?.dataset?.confirmLabel || "Confirm"
    cancelBtn.textContent = trigger?.dataset?.cancelLabel || "Cancel"

    // Red confirm button when the prompt is about destroying something.
    const danger = /\b(delete|remove|cancel|discard|unlock|call off)\b/i.test(message || "")
    confirmBtn.className =
      "px-3 py-1.5 text-[13px] font-medium text-white rounded-lg cursor-pointer " +
      (danger ? "bg-rose-600 hover:bg-rose-700" : "bg-brand hover:bg-brand-dark")

    const cleanup = (result) => {
      overlay.classList.add("hidden")
      confirmBtn.removeEventListener("click", onConfirm)
      cancelBtn.removeEventListener("click", onCancel)
      overlay.removeEventListener("mousedown", onBackdrop)
      document.removeEventListener("keydown", onKey)
      resolve(result)
    }
    const onConfirm = () => cleanup(true)
    const onCancel = () => cleanup(false)
    const onBackdrop = (e) => { if (e.target === overlay) cleanup(false) }
    const onKey = (e) => { if (e.key === "Escape") cleanup(false) }

    confirmBtn.addEventListener("click", onConfirm)
    cancelBtn.addEventListener("click", onCancel)
    overlay.addEventListener("mousedown", onBackdrop)
    document.addEventListener("keydown", onKey)

    overlay.classList.remove("hidden")
    // Focus the confirm button so Enter confirms / Tab reaches Cancel.
    confirmBtn.focus()
  })
}
